// LUMINA · pages/vajra/admin/RegistrationsSection.jsx
// Phase 120: 报名管理 — 活动分组 + 内联编辑 + CSV导出 + 报表
//
// 以活动为核心组织报名，管理员可以：
//   - 按活动分组查看报名记录（可折叠）
//   - 快速编辑姓名/邮箱/电话/备注（内联编辑）
//   - 一键确认/取消/标记付款
//   - 导出CSV通讯录
//   - 查看汇总报表

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API, GD, DG, PANEL, GDB } from '../constants';

/* ═══════════════════════════════════════════════════════════════════════════
   helpers
   ═══════════════════════════════════════════════════════════════════════════ */

function fmtDate(d) { if (!d) return ''; return d.slice(0,10); }

function groupByEvent(regs) {
  const map = {};
  for (const r of regs) {
    const eid = r.event_id;
    if (!map[eid]) {
      map[eid] = {
        event_id: eid,
        event_title: r.event_title || `Event #${eid}`,
        event_date: r.event_date || '',
        event_slug: r.event_slug || '',
        registrations: [],
        stats: { total: 0, confirmed: 0, pending: 0, cancelled: 0, paid: 0, unpaid: 0, waived: 0, amount: 0 },
      };
    }
    const g = map[eid];
    g.registrations.push(r);
    g.stats.total++;
    const st = r.status || 'pending';
    if (st === 'confirmed') g.stats.confirmed++;
    else if (st === 'cancelled') g.stats.cancelled++;
    else g.stats.pending++;
    const ps = r.payment_status || 'pending';
    if (ps === 'confirmed') { g.stats.paid++; g.stats.amount += (r.payment_amount || 0); }
    else if (ps === 'waived') g.stats.waived++;
    else g.stats.unpaid++;
  }
  return Object.values(map).sort((a, b) => -a.stats.total);
}

const STATUS_LABEL = { pending: '待处理', confirmed: '已确认', cancelled: '已取消' };
const STATUS_COLOR = { pending: '#D97706', confirmed: '#059669', cancelled: '#9CA3AF' };
const PAYMENT_LABEL = { pending: '待付款', confirmed: '已付款', waived: '已减免' };
const PAYMENT_COLOR = { pending: '#D97706', confirmed: '#059669', waived: '#6B7280' };

/* ═══════════════════════════════════════════════════════════════════════════
   component
   ═══════════════════════════════════════════════════════════════════════════ */

export default function RegistrationsSection({ token, headers }) {
  const V = `${API}/vajra`;

  // ── data ──
  const [regs, setRegs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');

  // ── expand / edit ──
  const [expanded, setExpanded] = useState({});     // event_id → bool
  const [editingId, setEditingId] = useState(null);  // reg id being edited inline
  const [editForm, setEditForm] = useState({});

  // ── filter ──
  const [filterEvent, setFilterEvent] = useState('');  // '' = all
  const [filterPayment, setFilterPayment] = useState('');
  const [searchText, setSearchText] = useState('');

  // ── report panel ──
  const [showReport, setShowReport] = useState(false);
  const [reportData, setReportData] = useState(null);

  /* ── fetch ────────────────────────────────────────────────────────────── */

  const loadRegistrations = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${V}/staff/registrations?full=true`, { headers });
      const data = r.ok ? await r.json() : [];
      setRegs(Array.isArray(data) ? data : []);
    } catch { setRegs([]); }
    setLoading(false);
  }, [V, token]);

  useEffect(() => { loadRegistrations(); }, [loadRegistrations]);

  /* ── grouped data ────────────────────────────────────────────────────── */

  const allGroups = useMemo(() => groupByEvent(regs), [regs]);

  // filter
  const groups = useMemo(() => {
    let g = allGroups;
    if (filterEvent) g = g.filter(g => String(g.event_id) === filterEvent);
    if (searchText) {
      const s = searchText.toLowerCase();
      g = g.map(g => ({
        ...g,
        registrations: g.registrations.filter(r =>
          (r.name || '').toLowerCase().includes(s) ||
          (r.email || '').toLowerCase().includes(s)
        ),
      })).filter(g => g.registrations.length > 0);
    }
    if (filterPayment) {
      g = g.map(g => ({
        ...g,
        registrations: g.registrations.filter(r => (r.payment_status || 'pending') === filterPayment),
      })).filter(g => g.registrations.length > 0);
    }
    return g;
  }, [allGroups, filterEvent, filterPayment, searchText]);

  // totals — both registration status and payment status dimensions
  const totals = useMemo(() => {
    let t = 0, c = 0, a = 0, paid = 0, unpaid = 0, waived = 0;
    for (const g of allGroups) {
      t += g.stats.total; c += g.stats.confirmed; a += g.stats.amount;
      paid += g.stats.paid; unpaid += g.stats.unpaid; waived += g.stats.waived;
    }
    return { total: t, confirmed: c, reg_unpaid: t - c, amount: a, paid, unpaid, waived };
  }, [allGroups]);

  /* ── actions ─────────────────────────────────────────────────────────── */

  const updateStatus = async (regId, status) => {
    const r = await fetch(`${V}/staff/registrations/${regId}/status`, {
      method: 'PUT', headers, body: JSON.stringify({ status }),
    });
    if (r.ok) { setMsg('✓ 状态已更新'); loadRegistrations(); }
    else { const d = await r.json().catch(() => ({})); setMsg('✗ ' + (d.detail || '更新失败')); }
  };

  const updatePayment = async (regId, paymentStatus, amount) => {
    const r = await fetch(`${V}/staff/registrations/${regId}/payment`, {
      method: 'PUT', headers, body: JSON.stringify({ payment_status: paymentStatus, payment_amount: amount }),
    });
    if (r.ok) { setMsg('✓ 付款状态已更新'); loadRegistrations(); }
    else { const d = await r.json().catch(() => ({})); setMsg('✗ ' + (d.detail || '更新失败')); }
  };

  const deleteReg = async (regId, name) => {
    if (!confirm(`确认删除 ${name} 的报名记录？`)) return;
    const r = await fetch(`${V}/staff/registrations/${regId}`, { method: 'DELETE', headers });
    if (r.ok) { setMsg('✓ 已删除'); loadRegistrations(); }
    else { const d = await r.json().catch(() => ({})); setMsg('✗ ' + (d.detail || '删除失败')); }
  };

  const saveEdit = async (regId) => {
    const r = await fetch(`${V}/staff/registrations/${regId}`, {
      method: 'PUT', headers, body: JSON.stringify(editForm),
    });
    if (r.ok) { setMsg('✓ 已保存'); setEditingId(null); loadRegistrations(); }
    else { const d = await r.json().catch(() => ({})); setMsg('✗ ' + (d.detail || '保存失败')); }
  };

  const startEdit = (reg) => {
    setEditingId(reg.id);
    setEditForm({ name: reg.name, email: reg.email, phone: reg.phone, pass_type: reg.pass_type, notes: reg.notes, payment_amount: reg.payment_amount });
  };

  const exportCSV = () => {
    const rows = [['活动', '姓名', '邮箱', '电话', 'Part', '状态', '付款', '金额']];
    for (const g of allGroups) {
      for (const r of g.registrations) {
        rows.push([
          g.event_title, r.name, r.email, r.phone, r.pass_type || '',
          STATUS_LABEL[r.status] || r.status, PAYMENT_LABEL[r.payment_status] || r.payment_status,
          r.payment_amount ? `$${r.payment_amount}` : '',
        ]);
      }
    }
    const csv = rows.map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'registrations.csv'; a.click();
    URL.revokeObjectURL(url);
    setMsg('✓ CSV 已导出');
  };

  const loadReport = async () => {
    try {
      const r = await fetch(`${V}/staff/registrations/report`, { headers });
      if (r.ok) setReportData(await r.json());
    } catch {}
    setShowReport(true);
  };

  const toggleExpand = (eid) => setExpanded(prev => ({ ...prev, [eid]: !prev[eid] }));
  const expandAll = () => { const e = {}; allGroups.forEach(g => e[g.event_id] = true); setExpanded(e); };
  const collapseAll = () => setExpanded({});

  /* ── inline styles ───────────────────────────────────────────────────── */

  const pill = (label, color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: 11, fontWeight: 600, color, background: color + '14', border: '1px solid ' + color + '33',
  });

  const btnPrimary = {
    padding: '6px 14px', background: DG, color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500,
  };
  const btnPrimarySmall = {
    padding: '5px 14px', background: DG, color: '#fff', border: 'none',
    borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 500,
  };
  const btnOutline = {
    padding: '6px 14px', background: 'transparent', color: GD,
    border: '1px solid ' + GD, borderRadius: 6, fontSize: 12, cursor: 'pointer',
  };
  const btnOutlineSmall = {
    padding: '5px 12px', background: 'transparent', color: GD,
    border: '1px solid ' + GD, borderRadius: 6, fontSize: 11, cursor: 'pointer',
  };
  const tinyBtn = {
    padding: '4px 10px', background: 'transparent', color: '#6B7280',
    border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 11, cursor: 'pointer',
  };
  const iconBtn = {
    padding: '3px 6px', background: 'transparent', border: '1px solid #E5E7EB',
    borderRadius: 4, fontSize: 12, cursor: 'pointer', color: '#6B7280',
  };
  const editInput = {
    width: '100%', padding: '5px 8px', border: '1px solid rgba(0,0,0,0.15)',
    borderRadius: 5, fontSize: 12, boxSizing: 'border-box',
  };
  const lbl = { display: 'block', fontSize: 10, color: '#999', marginBottom: 2, letterSpacing: .5 };

  const inputStyle = (w) => ({
    padding: '6px 10px', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 6,
    fontSize: 12, background: '#fff', width: w,
  });

  /* ── render ──────────────────────────────────────────────────────────── */

  return (
    <div style={{ background: PANEL, borderRadius: 12, padding: '20px', border: '1px solid rgba(0,0,0,0.06)' }}>
      {/* header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontSize: 14, color: GD, fontWeight: 600, marginBottom: 4 }}>报名管理</div>
          <div style={{ fontSize: 11, color: '#999' }}>按活动分组 · 内联编辑 · 导出报表</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportCSV} style={btnOutline}>📥 导出CSV</button>
          <button onClick={loadReport} style={btnOutline}>📊 报表</button>
          <button onClick={loadRegistrations} disabled={loading} style={btnPrimary}>
            {loading ? '加载中…' : '🔄 刷新'}
          </button>
        </div>
      </div>

      {/* message */}
      {msg && (
        <div style={{
          fontSize: 12, padding: '6px 12px', borderRadius: 6, marginBottom: 12,
          background: msg.startsWith('✓') ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)',
          color: msg.startsWith('✓') ? '#059669' : '#DC2626',
        }}>
          {msg}
          <button onClick={() => setMsg('')} style={{ marginLeft: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', fontSize: 14 }}>✕</button>
        </div>
      )}

      {/* stats bar — registration status (left) + payment status (right) */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { label: '总报名', val: totals.total },
          { label: '已确认', val: totals.confirmed, color: '#059669' },
          { label: '已付款', val: totals.paid, color: '#059669' },
          { label: '待付款', val: totals.unpaid, color: '#D97706' },
          { label: '已收款', val: `$${totals.amount.toFixed(0)}`, color: '#1D4ED8' },
        ].map(s => (
          <div key={s.label} style={{ textAlign: 'center', padding: '8px 16px', borderRadius: 8, background: 'rgba(0,0,0,0.03)' }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: s.color || '#333' }}>{s.val}</div>
            <div style={{ fontSize: 10, color: '#999', letterSpacing: .5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={filterEvent} onChange={e => setFilterEvent(e.target.value)}
          style={inputStyle(140)}>
          <option value="">全部活动</option>
          {allGroups.map(g => (
            <option key={g.event_id} value={String(g.event_id)}>{g.event_title}</option>
          ))}
        </select>
        <select value={filterPayment} onChange={e => setFilterPayment(e.target.value)}
          style={inputStyle(110)}>
          <option value="">全部付款</option>
          <option value="pending">待付款</option>
          <option value="confirmed">已付款</option>
          <option value="waived">已减免</option>
        </select>
        <input placeholder="搜索姓名/邮箱…" value={searchText} onChange={e => setSearchText(e.target.value)}
          style={{ ...inputStyle(180), flex: '1 1 160px', maxWidth: 240 }} />
        <button onClick={expandAll} style={tinyBtn}>▸ 全部展开</button>
        <button onClick={collapseAll} style={tinyBtn}>▸ 全部收起</button>
      </div>

      {/* registration groups */}
      {loading && <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>加载中…</div>}

      {!loading && groups.length === 0 && (
        <div style={{ textAlign: 'center', padding: 32, color: '#ccc', fontSize: 13 }}>暂无报名记录</div>
      )}

      {!loading && groups.map(g => {
        const open = expanded[g.event_id] !== false; // default open
        return (
          <div key={g.event_id} style={{
            marginBottom: 12, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10,
            overflow: 'hidden',
          }}>
            {/* event header */}
            <div onClick={() => toggleExpand(g.event_id)} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px', background: 'rgba(0,0,0,0.02)', cursor: 'pointer',
              userSelect: 'none',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1F2937' }}>
                  {open ? '▾' : '▸'} {g.event_title}
                  <span style={{ fontSize: 12, color: GD, marginLeft: 8, fontWeight: 400 }}>
                    {g.stats.total}人报名
                  </span>
                </div>
                {g.event_date && <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>{fmtDate(g.event_date)}</div>}
              </div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, fontSize: 11, flexWrap: 'wrap' }}>
                {g.stats.confirmed > 0 && <span style={pill('已确认 ' + g.stats.confirmed, '#059669')}>✓ {g.stats.confirmed}</span>}
                {g.stats.pending > 0 && <span style={pill('待处理 ' + g.stats.pending, '#D97706')}>⏳ {g.stats.pending}</span>}
                {g.stats.cancelled > 0 && <span style={pill('已取消 ' + g.stats.cancelled, '#9CA3AF')}>✕ {g.stats.cancelled}</span>}
                {g.stats.amount > 0 && <span style={pill('$' + g.stats.amount.toFixed(0), '#1D4ED8')}>${g.stats.amount.toFixed(0)}</span>}
              </div>
            </div>

            {/* registration table */}
            {open && (
              <div>
                {g.registrations.map(reg => {
                  const isEditing = editingId === reg.id;
                  return (
                    <div key={reg.id}>
                      {!isEditing ? (
                        /* ── normal row ── */
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 16px', borderTop: '1px solid rgba(0,0,0,0.04)',
                          fontSize: 13,
                        }}>
                          <div style={{ flex: '0 0 100px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {reg.name}
                          </div>
                          <div style={{ flex: '1 1 180px', color: '#6B7280', fontSize: 12, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {reg.email}
                          </div>
                          <div style={{ flex: '0 0 90px', color: '#999', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {reg.phone || '—'}
                          </div>
                          <div style={{ flex: '0 0 60px', fontSize: 11, color: '#6B7280' }}>
                            {reg.pass_type || '—'}
                          </div>
                          <div style={{ flex: '0 0 140px', display: 'flex', gap: 4, alignItems: 'center' }}>
                            {/* payment quick toggle */}
                            <select value={reg.payment_status || 'pending'}
                              onChange={e => updatePayment(reg.id, e.target.value, reg.payment_amount)}
                              style={{
                                fontSize: 11, padding: '2px 4px', borderRadius: 4,
                                border: `1px solid ${PAYMENT_COLOR[reg.payment_status] || '#ccc'}33`,
                                color: PAYMENT_COLOR[reg.payment_status] || '#999',
                                background: (PAYMENT_COLOR[reg.payment_status] || '#ccc') + '08',
                                cursor: 'pointer',
                              }}>
                              <option value="pending">⏳ 待付</option>
                              <option value="confirmed" style={{ color: '#059669' }}>✓ 已付</option>
                              <option value="waived" style={{ color: '#6B7280' }}>— 减免</option>
                            </select>
                            <input
                              key={`amt-${reg.id}-${reg.payment_amount || 0}`}
                              type="number"
                              min="0"
                              step="0.01"
                              defaultValue={reg.payment_amount || ''}
                              placeholder="$0"
                              onBlur={e => {
                                const v = e.target.value ? parseFloat(e.target.value) : 0;
                                updatePayment(reg.id, reg.payment_status || 'pending', v);
                              }}
                              style={{
                                width: 52, fontSize: 11, padding: '2px 4px', borderRadius: 4,
                                border: '1px solid rgba(0,0,0,0.15)', color: '#059669',
                                background: '#fff', textAlign: 'right',
                              }}
                            />
                          </div>
                          <div style={{ flex: '0 0 70px' }}>
                            <span style={{ ...pill(STATUS_LABEL[reg.status], STATUS_COLOR[reg.status]) }}>
                              {STATUS_LABEL[reg.status] || reg.status}
                            </span>
                          </div>
                          <div style={{ flex: '0 0 auto', display: 'flex', gap: 4 }}>
                            <button onClick={() => startEdit(reg)} title="编辑" style={iconBtn}>✎</button>
                            {reg.status !== 'confirmed' && (
                              <button onClick={() => updateStatus(reg.id, 'confirmed')} title="确认" style={{ ...iconBtn, color: '#059669' }}>✓</button>
                            )}
                            {reg.status !== 'cancelled' && (
                              <button onClick={() => updateStatus(reg.id, 'cancelled')} title="取消" style={{ ...iconBtn, color: '#DC2626' }}>✕</button>
                            )}
                            <button onClick={() => deleteReg(reg.id, reg.name)} title="删除" style={{ ...iconBtn, color: '#9CA3AF' }}>🗑</button>
                          </div>
                        </div>
                      ) : (
                        /* ── inline edit row ── */
                        <div style={{
                          padding: '10px 16px', borderTop: '1px solid rgba(0,0,0,0.04)',
                          background: 'rgba(251,191,36,0.04)',
                        }}>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                            <div style={{ flex: '1 1 100px', minWidth: 80 }}>
                              <label style={lbl}>姓名</label>
                              <input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                style={editInput} />
                            </div>
                            <div style={{ flex: '2 1 180px', minWidth: 140 }}>
                              <label style={lbl}>邮箱</label>
                              <input value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                                style={editInput} />
                            </div>
                            <div style={{ flex: '1 1 100px', minWidth: 80 }}>
                              <label style={lbl}>电话</label>
                              <input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })}
                                style={editInput} />
                            </div>
                            <div style={{ flex: '0 0 100px' }}>
                              <label style={lbl}>Part</label>
                              <input value={editForm.pass_type || ''} onChange={e => setEditForm({ ...editForm, pass_type: e.target.value })}
                                style={editInput} placeholder="第一部分" />
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <label style={lbl}>备注</label>
                              <input value={editForm.notes || ''} onChange={e => setEditForm({ ...editForm, notes: e.target.value })}
                                style={editInput} placeholder="如：随喜$50、需接送…" />
                            </div>
                            <div style={{ flex: '0 0 90px' }}>
                              <label style={lbl}>金额</label>
                              <input type="number" min="0" step="0.01"
                                value={editForm.payment_amount ?? ''}
                                onChange={e => setEditForm({ ...editForm, payment_amount: e.target.value ? parseFloat(e.target.value) : null })}
                                style={editInput} placeholder="$0" />
                            </div>
                            <button onClick={() => saveEdit(reg.id)} style={btnPrimarySmall}>保存</button>
                            <button onClick={() => setEditingId(null)} style={btnOutlineSmall}>取消</button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {/* ── Report Slide Panel ── */}
      {showReport && (
        <div onClick={() => setShowReport(false)} style={{
          position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.4)',
          display: 'flex', justifyContent: 'flex-end',
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: 'min(480px, 92vw)', height: '100%', background: '#fff',
            padding: '24px', overflow: 'auto', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, color: '#1F2937' }}>📊 报名报表</h3>
              <button onClick={() => setShowReport(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999' }}>✕</button>
            </div>

            {reportData ? (
              <>
                {/* Summary */}
                <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
                  {[
                    { label: '总报名', val: reportData.totals?.total_registrations || 0 },
                    { label: '已确认', val: reportData.totals?.total_confirmed || 0, color: '#059669' },
                    { label: '总收款', val: `$${(reportData.totals?.total_amount || 0).toFixed(0)}`, color: '#1D4ED8' },
                  ].map(s => (
                    <div key={s.label} style={{ flex: 1, textAlign: 'center', padding: '10px 8px', borderRadius: 8, background: 'rgba(0,0,0,0.03)' }}>
                      <div style={{ fontSize: 22, fontWeight: 700, color: s.color || '#333' }}>{s.val}</div>
                      <div style={{ fontSize: 10, color: '#999' }}>{s.label}</div>
                    </div>
                  ))}
                </div>

                {/* Per-event table */}
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>按活动汇总</div>
                <div style={{ marginBottom: 24 }}>
                  {(reportData.events || []).map(ev => (
                    <div key={ev.event_id} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '8px 0', borderBottom: '1px solid rgba(0,0,0,0.06)', fontSize: 13,
                    }}>
                      <div style={{ flex: 1, color: '#333', fontWeight: 500 }}>{ev.event_title}</div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 12, color: '#6B7280' }}>
                        <span>{ev.total}人</span>
                        <span style={{ color: '#059669' }}>已付{ev.payment_confirmed}</span>
                        <span style={{ color: '#1D4ED8' }}>${(ev.total_amount || 0).toFixed(0)}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Contact list */}
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 8 }}>
                  已确认学员通讯录 ({reportData.contacts?.length || 0}人)
                </div>
                <button onClick={() => {
                  const emails = (reportData.contacts || []).map(c => c.email).join(', ');
                  navigator.clipboard.writeText(emails).then(() => setMsg('✓ 邮箱已复制')).catch(() => {});
                }} style={{ ...btnOutlineSmall, marginBottom: 10 }}>
                  📋 复制所有邮箱
                </button>
                <div style={{ maxHeight: 300, overflow: 'auto' }}>
                  {(reportData.contacts || []).map((c, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', padding: '6px 0',
                      borderBottom: '1px solid rgba(0,0,0,0.03)', fontSize: 12,
                    }}>
                      <span style={{ fontWeight: 500, color: '#333' }}>{c.name}</span>
                      <span style={{ color: '#6B7280' }}>{c.email}</span>
                      {c.phone && <span style={{ color: '#999', marginLeft: 8 }}>{c.phone}</span>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>加载中…</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
